import {
  CellId,
  GridElementEntityId,
  StaticCellId,
  sleep,
  uuid,
} from "@hex/common";
import { isEqual } from "lodash";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";

import { useDispatch, useSelector, useStore } from "../../redux/hooks";
import { hexVersionMPSelectors } from "../../redux/slices/hexVersionMPSlice.js";
import {
  setScrolledToViewCellId,
  setSelectedCellIds,
} from "../../redux/slices/logicViewSlice";
import { customScrollIntoView } from "../../util/customScrollIntoView.js";
import { useProjectContext } from "../../util/projectContext.js";
import { getValueFromReduxOrStorage } from "../useBrowserStorage.js";
import {
  LocalStorageKeys,
  useDynamicLocalStorageSetter,
} from "../useLocalStorage.js";

export interface CellScrollAPIOptions {
  /**
   * Determines which part of a cell to scroll to in the logic view
   */
  scrollTarget?:
    | "source"
    | "output"
    | { type: "lineNumber"; lineNumber: number };
  /**
   * What kind of scroll to do, @see scroll-into-view-if-needed
   */
  scrollBehavior?: ScrollBehavior;
  /**
   * Wait until all cells have finished adjusting in size for layout before scrolling.
   * Useful for avoiding uneeded jumps when initially loading.
   */
  waitForSizeChanges?: boolean;
  /**
   * Timing between checking heights to see if they have changed for delaying scroll. Useful for fine tuning
   * when different views may take a little longer to load
   */
  waitForSizeChangeDelay?: number;
}

/**
 * The mechanism for scrolling to a cell changes depending on virtualization
 * so we dynamically swap out the implementation. We also support scrolling
 * in multiple locations as well.
 */
export interface CellScrollApi {
  scrollToCellById?: (cellId: CellId, options?: CellScrollAPIOptions) => void;
  scrollToCellByStaticId?: (
    staticCellId: StaticCellId,
    options?: CellScrollAPIOptions,
  ) => void;
}

class CellScrollApiWrapper {
  private apis: Record<string, CellScrollApi> = {};

  public addApi(api: CellScrollApi): { removeApi: () => void } {
    const id = uuid();
    this.apis[id] = api;
    const removeApi = (): void => {
      delete this.apis[id];
    };
    return { removeApi };
  }

  public scrollToCellById(
    cellId: CellId,
    options?: CellScrollAPIOptions,
  ): void {
    for (const api of Object.values(this.apis)) {
      api.scrollToCellById?.(cellId, options);
    }
  }

  public scrollToCellByStaticId(
    staticCellId: StaticCellId,
    options?: CellScrollAPIOptions,
  ): void {
    for (const api of Object.values(this.apis)) {
      api.scrollToCellByStaticId?.(staticCellId, options);
    }
  }
}

const CellScrollApiContext =
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- for dev display name
  createContext<CellScrollApiWrapper | undefined>(undefined);
CellScrollApiContext.displayName = "CellScrollApiContext";

/**
 * Root context for regiestering / calling different cell scroll APIs.
 * Needs to be added to the component tree at least once.
 *
 * Useful to add again if trying to completly override scroll behaviour for a sub tree.
 */
export const CellScrollApiContextProvider: React.ComponentType<{
  children: ReactNode;
}> = ({ children }) => {
  const [apiWrapper] = useState(() => new CellScrollApiWrapper());

  return (
    <CellScrollApiContext value={apiWrapper}>{children}</CellScrollApiContext>
  );
};

/**
 * Add an additional cell scroll api to the context.
 *
 * Add api instances are called when `scrollToCellById` is invoked on the wrapper.
 * This allows for scrolling in multiple views concurrently using different methods.
 *
 * @returns a remove callback that should be called to prevent memory leaks
 */
export const useAddCellScrollApi = (): CellScrollApiWrapper["addApi"] => {
  const wrapper = useContext(CellScrollApiContext);
  return useCallback(
    (newApi) => {
      if (wrapper == null) {
        console.warn(
          "Cell scroll api context not found, make sure a provider was added",
        );
        return { removeApi: () => {} };
      } else {
        return wrapper.addApi(newApi);
      }
    },
    [wrapper],
  );
};

/**
 * Scroll to a given cell.
 *
 * Uses all registered scroll api's to allow for scrolling in multiple views concurrently.
 * Will correctly use virtualization scroll methods if needed.
 */
export const useScrollToCell = (): ((
  cellId: CellId,
  options?: CellScrollAPIOptions & { animateFocus?: boolean },
) => void) => {
  const wrapper = useContext(CellScrollApiContext);
  const dispatch = useDispatch();
  const store = useStore();
  const { hexVersionId } = useProjectContext();
  const localStorageSetter = useDynamicLocalStorageSetter();

  return useCallback(
    (cellId, options) => {
      if (wrapper == null) {
        console.warn(
          "Cell scroll api context not found, make sure a provider was added",
        );
      } else {
        const state = store.getState();
        const cellIdToAncestorIds = hexVersionMPSelectors
          .getCellSelectors(hexVersionId)
          .selectCellIdToAncestorIds(state);

        const cellIdsToUncollapse = new Set<CellId>();
        for (const ancestorId of cellIdToAncestorIds[cellId] ?? []) {
          const isCollapsed = getValueFromReduxOrStorage(
            state,
            LocalStorageKeys.CELL_SOURCE_COLLAPSED(ancestorId),
            "Local",
          );
          if (isCollapsed) {
            cellIdsToUncollapse.add(ancestorId);
          }
        }

        // In the case we are trying to scroll to a specific line number, we should
        // also uncollapse the source
        if (
          options != null &&
          typeof options.scrollTarget !== "string" &&
          options.scrollTarget?.type === "lineNumber"
        ) {
          const isCollapsed = getValueFromReduxOrStorage(
            state,
            LocalStorageKeys.CELL_SOURCE_COLLAPSED(cellId),
            "Local",
          );
          if (isCollapsed) {
            cellIdsToUncollapse.add(cellId);
          }
        }

        for (const cellIdToUncollapse of cellIdsToUncollapse) {
          localStorageSetter(
            LocalStorageKeys.CELL_SOURCE_COLLAPSED(cellIdToUncollapse),
            false,
          );
        }

        wrapper.scrollToCellById(cellId, options);
        if (options?.animateFocus) {
          // If we want to animate the focus for a scrolled to cell, we should clear
          // the other selected cellIds.
          dispatch(setSelectedCellIds([]));
          dispatch(setScrolledToViewCellId(cellId));
        }
      }
    },
    [wrapper, store, hexVersionId, localStorageSetter, dispatch],
  );
};

/**
 * Selector hook to determine if a cell is currently scrolled to.
 *
 * @param cellId The ID of the cell to check
 * @returns A boolean indicating if the cell is scrolled to. We use this to
 * determine if we should have a cell animated as focused / selected after it is chosen
 * from the project search sidebar.
 */
export const useCellIdIsScrolledIntoView = (cellId: CellId): boolean => {
  return useSelector(
    (state) => state.logicView.scrolledToViewCellId === cellId,
  );
};

/**
 * Scroll to a given cell by static id.
 *
 * Uses all registered scroll api's to allow for scrolling in multiple views concurrently.
 * Will correctly use virtualization scroll methods if needed.
 */
export const useScrollToCellByStaticId =
  (): CellScrollApiWrapper["scrollToCellByStaticId"] => {
    const wrapper = useContext(CellScrollApiContext);
    return useCallback(
      (staticCellId, options) => {
        if (wrapper == null) {
          console.warn(
            "Cell scroll api context not found, make sure a provider was added",
          );
        } else {
          wrapper.scrollToCellByStaticId(staticCellId, options);
        }
      },
      [wrapper],
    );
  };

/**
 * Cell Scroll Utils
 */

/**
 * Get a stable DOM id to an anchor tag associated with a cell
 */
export function getCellAnchorId(cellId: CellId): string {
  return `cellAnchor-${cellId}`;
}

/**
 * Get a stable DOM id to an anchor tag associated with a cell in diff view
 */
export function getDiffCellAnchorId(staticCellId: StaticCellId): string {
  return `cellDiffAnchor-${staticCellId}`;
}

/**
 * Get a stable DOM id to an anchor tag associated with an app element
 */
export function getAppElementAnchorId(entityId: GridElementEntityId): string {
  return `appCellAnchor-${entityId}`;
}

/**
 * Get a stable DOM id to a scroll container for cells
 */
export function getCellScrollParentId(
  location: "app" | "logic" | "diff",
): string {
  return `cellScrollParent-${location}`;
}

const checkScrollHeight = (location: "app" | "logic" | "diff"): number[] => {
  const eles = document.querySelectorAll(getCellScrollParentId(location));
  const heights = Array.from(eles).map((ele) => ele.scrollHeight);

  return heights;
};

const maybeDelayScroll = async ({
  delay,
  delaySeconds = 500,
  location,
}: {
  delay: boolean;
  delaySeconds?: number;
  location: "app" | "logic" | "diff";
}): Promise<void> => {
  if (!delay) {
    return;
  }

  const ITERATION_DELAY = delaySeconds;
  const NUM_ITERATIONS = 3;

  // If the scroll height of any cell scroll container is changing, don't start the scroll yet.
  let currentHeight = checkScrollHeight(location);
  for (let i = 0; i < NUM_ITERATIONS; ++i) {
    // eslint-disable-next-line no-await-in-loop -- we want to iteratively wait in this case
    await sleep(ITERATION_DELAY);
    const newHeight = checkScrollHeight(location);
    if (isEqual(currentHeight, newHeight)) {
      break;
    }
    currentHeight = newHeight;
  }
};

/**
 * Offset is so we don't cut off the top of the selected cell outline
 */
export const CELL_OUTLINE_OFFSET = 10;

/**
 * Offset the scroll position so the targeted line is in view
 * Each line in the monaco editor is 18px tall, so approximate the offset
 */
export const getMonacoLineNumberPxScrollOffset = (
  lineNumber: number,
): number => {
  return lineNumber * 18 - CELL_OUTLINE_OFFSET;
};

/**
 * You most likely want to use `useScrollToCell` over this to properly account for virtualization
 *
 * Scrolls to a cell in the logic view using conventional DOM methods
 */
export const scrollToLogicCellNonVirtualzed = ({
  cellId,
  onFinished,
  options: {
    scrollBehavior = "smooth",
    scrollTarget = "source",
    waitForSizeChangeDelay,
    waitForSizeChanges,
  },
}: {
  cellId: CellId;
  options: CellScrollAPIOptions;
  onFinished?: () => void;
}): void => {
  void maybeDelayScroll({
    delay: waitForSizeChanges === true,
    delaySeconds: waitForSizeChangeDelay,
    location: "logic",
  }).then(() => {
    const cellAnchorElement = document.getElementById(getCellAnchorId(cellId));

    if (cellAnchorElement) {
      const logicScrollParent = cellAnchorElement.closest<HTMLElement>(
        `#${getCellScrollParentId("logic")}`,
      );
      if (scrollTarget === "output" || scrollTarget === "source") {
        customScrollIntoView(
          cellAnchorElement,
          {
            boundary: logicScrollParent ?? undefined,
            scrollMode: "if-needed",
            behavior: scrollBehavior,
            block: scrollTarget === "output" ? "end" : "start",
            offsetTop: -CELL_OUTLINE_OFFSET,
          },
          onFinished,
        );
      } else {
        customScrollIntoView(
          cellAnchorElement,
          {
            boundary: logicScrollParent ?? undefined,
            scrollMode: "always",
            behavior: scrollBehavior,
            block: "start",
            offsetTop: getMonacoLineNumberPxScrollOffset(
              scrollTarget.lineNumber,
            ),
          },
          onFinished,
        );
      }
    }
  });
};

/**
 * Scrolls to a cell in a diff view using conventional DOM methods
 */
export const scrollToDiffCell = ({
  options: {
    scrollBehavior = "smooth",
    waitForSizeChangeDelay,
    waitForSizeChanges,
  },
  staticCellId,
}: {
  staticCellId: StaticCellId;
  options: CellScrollAPIOptions;
}): void => {
  void maybeDelayScroll({
    delay: waitForSizeChanges === true,
    delaySeconds: waitForSizeChangeDelay,
    location: "diff",
  }).then(() => {
    const cellAnchorElement = document.getElementById(
      getDiffCellAnchorId(staticCellId),
    );
    if (cellAnchorElement) {
      customScrollIntoView(cellAnchorElement, {
        scrollMode: "if-needed",
        behavior: scrollBehavior,
        block: "center",
      });
    }
  });
};

/**
 * Scrolls to an app element conventional DOM methods
 */
export const scrollToAppElement = ({
  entityId,
  options: {
    scrollBehavior = "smooth",
    waitForSizeChangeDelay,
    waitForSizeChanges,
  },
}: {
  entityId: GridElementEntityId;
  options: {
    scrollBehavior?: ScrollBehavior;
    waitForSizeChanges?: boolean;
    waitForSizeChangeDelay?: number;
  };
}): void => {
  void maybeDelayScroll({
    delay: waitForSizeChanges === true,
    location: "app",
    delaySeconds: waitForSizeChangeDelay,
  }).then(() => {
    const appAnchorElement = document.getElementById(
      getAppElementAnchorId(entityId),
    );
    if (appAnchorElement) {
      customScrollIntoView(appAnchorElement, {
        scrollMode: "if-needed",
        behavior: scrollBehavior,
        block: "center",
        offsetTop: -CELL_OUTLINE_OFFSET,
      });
    }
  });
};
